import { SITE_NAME } from '../constants/pageMeta'
import {
  buildOrganizationJsonLd,
  getAbsoluteUrl,
  getOgImageUrl,
  getSiteOrigin,
  OG_IMAGE_PATH,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_TYPE
} from '../constants/site'

function setMetaTag(attr, key, content) {
  if (!content) return

  let el = document.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLinkTag(rel, href) {
  if (!href) return

  let el = document.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setJsonLd(id, data) {
  const existing = document.getElementById(id)
  if (!data) {
    existing?.remove()
    return
  }

  let el = existing
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

function setOgImageTags(imageUrl, meta = {}) {
  const width = meta.ogImageWidth || OG_IMAGE_WIDTH
  const height = meta.ogImageHeight || OG_IMAGE_HEIGHT
  const type = meta.ogImageType || OG_IMAGE_TYPE
  const alt = meta.ogImageAlt || `${SITE_NAME} logo`

  setMetaTag('property', 'og:image', imageUrl)
  if (imageUrl.startsWith('https://')) {
    setMetaTag('property', 'og:image:secure_url', imageUrl)
  }
  setMetaTag('property', 'og:image:width', String(width))
  setMetaTag('property', 'og:image:height', String(height))
  setMetaTag('property', 'og:image:type', type)
  setMetaTag('property', 'og:image:alt', alt)

  setMetaTag('name', 'twitter:image', imageUrl)
  setMetaTag('name', 'twitter:image:alt', alt)
}

export function applyPageMeta(meta = {}) {
  const origin = getSiteOrigin()
  const title = meta.title || SITE_NAME
  const description = meta.description || ''
  const canonicalPath = meta.canonicalPath || window.location.pathname
  const canonicalUrl = origin ? `${origin}${canonicalPath}` : canonicalPath
  const imagePath = meta.ogImage || OG_IMAGE_PATH
  const imageUrl = getAbsoluteUrl(imagePath)
  const ogTitle = meta.ogTitle || title
  const ogDescription = meta.ogDescription || description

  document.title = title
  setMetaTag('name', 'description', description)
  setMetaTag('name', 'robots', meta.robots || 'index, follow')

  setMetaTag('property', 'og:title', ogTitle)
  setMetaTag('property', 'og:description', ogDescription)
  setMetaTag('property', 'og:type', meta.ogType || 'website')
  setMetaTag('property', 'og:url', canonicalUrl)
  setMetaTag('property', 'og:site_name', SITE_NAME)
  setOgImageTags(imageUrl, meta)

  setMetaTag('name', 'twitter:card', meta.twitterCard || 'summary_large_image')
  setMetaTag('name', 'twitter:title', ogTitle)
  setMetaTag('name', 'twitter:description', ogDescription)

  setLinkTag('canonical', canonicalUrl)

  if (meta.jsonLd) {
    setJsonLd('page-json-ld', meta.jsonLd)
  } else if (meta.includeJsonLd) {
    setJsonLd('page-json-ld', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: ogTitle,
      description: ogDescription,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: origin
      }
    })
  } else {
    setJsonLd('page-json-ld', null)
  }

  if (meta.includeOrganizationJsonLd) {
    setJsonLd('organization-json-ld', buildOrganizationJsonLd(origin, getOgImageUrl()))
  } else {
    setJsonLd('organization-json-ld', null)
  }
}
